import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TransportSedelQueryKey } from "api/transportSedel.api";
import { createUppdragInspection, UppdragQueryKey } from "api/uppdrag.api";
import { RadioCardGroupItem } from "components/RadioCardGroup";
import { useNotification } from "hooks/useNotification";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { InspectionType } from "types/enums";
import { getSeparatorTypeLabel } from "types/separator/separator.helpers";
import { isAvskiljare, SeparatorType } from "types/separator/separator.type";
import { Uppdrag } from "types/uppdrag.type";
import { z } from "zod";

const uppgiftDropdownItems: RadioCardGroupItem[] = [
  {
    id: InspectionType.Slamtomning,
    label: "Avfallstransport",
  },
  {
    id: InspectionType.SixMonths,
    label: "6-månaderskontroll",
  },
  {
    id: InspectionType.FiveYears,
    label: "5-årsbesiktning",
  },
  {
    id: InspectionType.WaterMeasurement,
    label: "Vattenprovtagning",
  },
];

export const createInspectionSchema = z.object({
  inspectionType: z
    .enum(
      Object.values(InspectionType) as [InspectionType, ...InspectionType[]],
      {
        errorMap: () => ({
          message:
            "Välj en av: " +
            Object.values(InspectionType)
              .filter((x) => x !== "Tömning")
              .join(", "),
        }),
      },
    )
    .optional() // Make it optional initially
    .refine((val) => !!val, {
      message: "Obligatoriskt fält",
    }), // But require it during submission
  separatorUuid: z.string().uuid().nullable().optional(),
});

export type TCreateInspectionSchema = {
  inspectionType?: InspectionType | null;
  separatorUuid?: string | null;
};

interface Input {
  uppdrag: Uppdrag;
  openWasteModal: () => void;
  openTransportSedelModal: () => void;
  setTomningSeparatorUuid?: Dispatch<SetStateAction<string | undefined>>;
}

export const useCreateUppgiftLogic = ({
  uppdrag,
  openWasteModal,
  openTransportSedelModal,
  setTomningSeparatorUuid,
}: Input) => {
  // const hasSetInititalSeparatorRef = useRef(false);
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [isUppgiftModalOpen, setIsUppgiftModalOpen] = useState(false);

  const form = useForm<TCreateInspectionSchema>({
    resolver: zodResolver(createInspectionSchema),
    defaultValues: {
      inspectionType: InspectionType.Slamtomning,
      // separatorUuid: uppdrag.facility.separatorList.find(() => true)?.uuid,
    },
  });

  const resetForm = () => form.reset();

  const onModalClose = () => setIsUppgiftModalOpen(false);
  const afterModalClose = () => resetForm();

  const mutation = useMutation({
    ...createUppdragInspection(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });
      onModalClose();
    },
    onError: () => {
      notification.error();
    },
  });

  const onSubmit = form.handleSubmit((data: TCreateInspectionSchema) => {
    // Validation: Require separator for non-Slamtomning inspection types
    if (
      data.inspectionType !== InspectionType.Slamtomning &&
      (!data.separatorUuid || data.separatorUuid === "")
    ) {
      form.setError("separatorUuid", {
        type: "manual",
        message: `Objekt krävs för ${data.inspectionType}`,
      });
      return;
    }

    // Find the selected separator
    const selectedSeparator = facilitySeparatorList.find(
      (s) => s.uuid === data.separatorUuid,
    );

    // Validation: WaterMeasurement requires WaterMeasurementPoint
    if (data.inspectionType === InspectionType.WaterMeasurement) {
      if (selectedSeparator?.type !== SeparatorType.WaterMeasurementPoint) {
        form.setError("separatorUuid", {
          type: "manual",
          message: "Provtagningspunkt krävs för Vattenprovtagning",
        });
        return;
      }
    }

    // Validation: WaterMeasurementPoint only allows WaterMeasurement
    if (data.inspectionType !== InspectionType.WaterMeasurement) {
      if (selectedSeparator?.type === SeparatorType.WaterMeasurementPoint) {
        form.setError("separatorUuid", {
          type: "manual",
          message: "Endast Vattenprovtagning tillåten för objektet",
        });
        return;
      }
    }

    if (data.inspectionType === InspectionType.Slamtomning) {
      onModalClose();
      uppdrag.transportSedelList.length > 0
        ? openWasteModal()
        : openTransportSedelModal();
      if (setTomningSeparatorUuid) {
        setTomningSeparatorUuid(data.separatorUuid as string);
      }
      return;
    }
    mutation.mutate({
      uppdragId: uppdrag.id,
      payload: data,
    });
  });

  const facilitySeparatorList = useMemo(
    () =>
      (uppdrag.facility.separatorList ?? []).sort((a, b) =>
        a.identifier < b.identifier ? -1 : 1,
      ),
    [uppdrag.facility.separatorList],
  );
  const selectedInspectionType = form.watch("inspectionType");

  const hasSelectedInspection =
    selectedInspectionType === InspectionType.FiveYears ||
    selectedInspectionType === InspectionType.SixMonths;

  const separatorDropdownItems = facilitySeparatorList.map((x) => ({
    label: `${x.identifier} - ${getSeparatorTypeLabel(x)}`,
    value: x.uuid,
    disabled:
      (!isAvskiljare(x) && hasSelectedInspection) ||
      (selectedInspectionType === InspectionType.WaterMeasurement &&
        x.type !== SeparatorType.WaterMeasurementPoint) ||
      (selectedInspectionType !== InspectionType.WaterMeasurement &&
        x.type === SeparatorType.WaterMeasurementPoint),
  }));
  const selectedSeparaturUuid = form.watch("separatorUuid");

  const selectedSeparator = facilitySeparatorList.find(
    (x) => x.uuid === selectedSeparaturUuid,
  );

  // A WaterMeasurementPoint only allows water measurement
  const isWaterMeasurementPointSelected =
    selectedSeparator?.type === SeparatorType.WaterMeasurementPoint;

  const uppgiftDropdownItemsWithDisabled = uppgiftDropdownItems.map((x) => ({
    ...x,
    isDisabled: isWaterMeasurementPointSelected
      ? x.id !== InspectionType.WaterMeasurement
      : !!selectedSeparator && x.id === InspectionType.WaterMeasurement,
  }));

  // Keep the selected uppgift compatible with the selected separator
  // (e.g. when a separator is created from the dropdown)
  useEffect(() => {
    if (!selectedSeparator) {
      return;
    }
    if (
      isWaterMeasurementPointSelected &&
      selectedInspectionType !== InspectionType.WaterMeasurement
    ) {
      form.setValue("inspectionType", InspectionType.WaterMeasurement);
    } else if (
      !isWaterMeasurementPointSelected &&
      selectedInspectionType === InspectionType.WaterMeasurement
    ) {
      form.setValue("inspectionType", InspectionType.Slamtomning);
    }
  }, [
    selectedSeparator,
    isWaterMeasurementPointSelected,
    selectedInspectionType,
    form,
  ]);

  // const isActionDisabled =
  //   !selectedInspectionType ||
  //   (!isAvskiljare(selectedSeparator) && hasSelectedInspection);

  return {
    form,
    mutation,
    separatorDropdownItems,
    isModalOpen: isUppgiftModalOpen,
    openModal: () => setIsUppgiftModalOpen(true),
    onSubmit,
    uppdrag,
    afterModalClose,
    onModalClose,
    uppgiftDropdownItems: uppgiftDropdownItemsWithDisabled,
    // isActionDisabled,
    isTomningSelected: selectedInspectionType === InspectionType.Slamtomning,
    isWaterMeasurementSelected:
      selectedInspectionType === InspectionType.WaterMeasurement,
    selectedSeparaturUuid,
    isSeparatorRequired:
      selectedInspectionType === InspectionType.FiveYears ||
      selectedInspectionType === InspectionType.SixMonths ||
      selectedInspectionType === InspectionType.WaterMeasurement,
    isWaterMeasurementPointRequired:
      selectedInspectionType === InspectionType.WaterMeasurement,
  };
};

export type CreateUppgiftLogicRetVal = ReturnType<typeof useCreateUppgiftLogic>;
